/**
 * Contracts module API — Notes (plan §5.6, §10.6, Appendix A "Notes", §13.8
 * WP-70). Typed calls over the shared axios instance (`services/api`), the
 * same convention `api/client.ts` uses — never axios directly (this repo's
 * jest cannot parse axios's ESM build unmocked; every module under
 * src/contracts reaches it only through the mocked services/api).
 *
 * A new, SEPARATE file rather than an addition to `api/client.ts`: another
 * WP appends to that file concurrently (orchestrator hand-off,
 * intake.ts/rateCard.ts precedent) — `notes.mount.md` notes a later
 * one-line re-export from `client.ts` as an optional follow-up, not
 * something this file or `components/notesPanel/**` depends on.
 */
import api from '../../services/api';

/** plan §5.6 cta_notes.origin. */
export type NoteOrigin = 'manual' | 'xlsx_comment' | 'xlsx_thread' | 'xlsx_cell_text' | 'docx_comment' | 'outcome';

/** plan §5.6 cta_notes.provenance — nulled for a manual/outcome note with no document locator. */
export interface NoteProvenance {
  file: string | null;
  version_id: string | null;
  sheet: string | null;
  cell: string | null;
  section: string | null;
  page: number | null;
}

/** plan §5.6 cta_notes row, as the API returns it. */
export interface Note {
  id: string;
  negotiation_id: string;
  version_id: string | null;
  budget_line_id: string | null;
  clause_id: string | null;
  cell_id: number | null;
  body: string;
  author_id: string | null;
  author_name: string | null;
  origin: NoteOrigin;
  noted_at: string;
  provenance: NoteProvenance;
  created_at: string;
  created_by: string | null;
}

/** GET /notes params — `negotiationId` is required; the rest narrow further (§13.8). */
export interface ListNotesParams {
  negotiationId: string;
  versionId?: string;
  budgetLineId?: string;
  clauseId?: string;
  cellId?: number;
}

/** GET /notes — notes for a negotiation, optionally narrowed to a version/budget line/clause/cell. */
export async function listNotes(params: ListNotesParams): Promise<Note[]> {
  const response = await api.get<{ notes: Note[] }>('/api/contracts/notes', { params });
  return response.data.notes;
}

/**
 * Input for POST /notes. `origin` defaults to 'manual' server-side; only
 * 'manual' or 'outcome' are accepted here — harvested origins are written
 * only by the parse runner (WP-29), never through this API.
 */
export interface CreateNoteInput {
  negotiationId: string;
  versionId?: string | null;
  budgetLineId?: string | null;
  clauseId?: string | null;
  cellId?: number | null;
  body: string;
  origin?: 'manual' | 'outcome';
  authorName?: string | null;
  provenance?: Partial<NoteProvenance>;
}

/** POST /notes. */
export async function createNote(input: CreateNoteInput): Promise<Note> {
  const response = await api.post<{ note: Note }>('/api/contracts/notes', input);
  return response.data.note;
}

/** PUT /notes/:id — body only; a harvested note (any origin but manual/outcome) is refused with 409. */
export async function updateNote(id: string, body: string): Promise<Note> {
  const response = await api.put<{ note: Note }>(`/api/contracts/notes/${id}`, { body });
  return response.data.note;
}

/** GET /notes/search params — at least one is required (the backend refuses an unfiltered search). */
export interface SearchNotesParams {
  q?: string;
  sponsorId?: string;
  entryId?: string;
  from?: string;
  to?: string;
}

/**
 * GET /notes/search — text over body, narrowed by sponsor (via the
 * negotiation's study), entry (via the note's budget line) and a date range
 * on noted_at (§10.6). Results are already scoped to the caller's site.
 */
export async function searchNotes(params: SearchNotesParams): Promise<Note[]> {
  const response = await api.get<{ notes: Note[] }>('/api/contracts/notes/search', { params });
  return response.data.notes;
}
